/**
 * Panel-state projection (Phase 4c). Aggregates the SEATED jurors' SpatchVenire
 * orientations and reads them against the model's asymmetric threshold: the
 * prosecution needs a ~two-thirds pre-deliberation majority, while the defense
 * needs only a durable minority (leniency/hung-jury drift favors acquittal near
 * even). Reference only — projections from probabilistic estimates, not answers.
 */

import { store, panelContext, svProfile } from '@/app/store';
import { scoreJurorSV } from '@/core/scoring-sv';

const LEAN = 2; // |orientation| >= LEAN counts as leaning that side

/** Render the seated-panel projection into #panel-state from current state. */
export function renderPanelState(): void {
  const host = document.getElementById('panel-state');
  if (!host) return;
  const st = store.getState();
  const ctx = panelContext();
  const profile = svProfile();
  const seated = st.jurors.filter((j) => j.status === 'seated');

  if (!seated.length) {
    host.innerHTML =
      '<div style="font-size:11px;color:var(--text-muted);padding:2px 0;">Panel state: no jurors seated yet — mark jurors “Seated” to see the projection.</div>';
    return;
  }

  let pros = 0;
  let def = 0;
  for (const j of seated) {
    const o = scoreJurorSV(j, ctx, profile).orientation;
    if (o >= LEAN) pros++;
    else if (o <= -LEAN) def++;
  }
  const neutral = seated.length - pros - def;
  const n = seated.length;
  const defFrac = def / n;
  const prosFrac = pros / n;

  // Asymmetric read.
  let read: string;
  let color: string;
  if (defFrac > 1 / 3) {
    read = 'Defense holds a durable minority so far — near-even panels drift toward acquittal.';
    color = '#16a34a';
  } else if (prosFrac >= 2 / 3) {
    read = 'Prosecution majority forming (toward the ~2/3 pre-deliberation threshold).';
    color = '#dc2626';
  } else {
    read = 'Contested — the neutrals decide it; probe them before spending strikes.';
    color = '#b45309';
  }

  const bar = (count: number, c: string): string =>
    `<span style="display:inline-block;height:8px;width:${Math.round((count / n) * 120)}px;background:${c};border-radius:2px;vertical-align:middle;"></span>`;

  host.innerHTML =
    '<div style="border:1px solid #e2e8f0;border-radius:10px;padding:9px 11px;margin:4px 0;background:#f8fafc;">' +
    `<div style="font-size:11px;font-weight:700;color:#334155;margin-bottom:4px;">Panel state — ${n} seated</div>` +
    `<div style="font-size:11px;color:var(--text-secondary);margin-bottom:5px;">` +
    `${bar(pros, '#dc2626')} ${pros} lean prosecution &nbsp; ${bar(neutral, '#cbd5e1')} ${neutral} neutral &nbsp; ${bar(def, '#16a34a')} ${def} lean defense</div>` +
    `<div style="font-size:11px;font-weight:600;color:${color};">${read}</div>` +
    '<div style="font-size:9px;color:var(--text-muted);margin-top:4px;">Reference only — projection from probabilistic estimates, not recorded answers.</div>' +
    '</div>';
}
